import { type SelectOption } from "@/components/SelectInput";

/**
 * The first-level administrative division within a Country — a US State, a UK
 * County, a Canadian Province.
 *
 * `code` is the value persisted to the API's free-form `state` field; `name` is
 * what the user sees. The two are matched interchangeably when reading a value
 * back, because Loqate returns a code for some countries and a name for others.
 */
export type Subdivision = {
  code: string;
  name: string;
};

export type SupportedCountry = {
  isoAlpha2: string;
  name: string;
  /** The country's own word for its Subdivision — "State", "Province". */
  subdivisionLabel: string;
  subdivisionRequired: boolean;
  subdivisions: readonly Subdivision[];
  /** The country's own word for its postcode — "ZIP Code", "Postal Code". */
  postCodeLabel: string;
  /** Whether Loqate's address lookup covers this country. */
  loqateSupported: boolean;
};

const toSubdivisions = (data: Record<string, string>): readonly Subdivision[] =>
  Object.entries(data).map(([code, name]) => ({ code, name }));

const US_STATES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
} as const;

const CANADIAN_PROVINCES = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NT: "Northwest Territories",
  NS: "Nova Scotia",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
} as const;

const MEXICAN_STATES = {
  AGU: "Aguascalientes",
  BCN: "Baja California",
  BCS: "Baja California Sur",
  CAM: "Campeche",
  CHP: "Chiapas",
  CHH: "Chihuahua",
  CMX: "Ciudad de México",
  COA: "Coahuila",
  COL: "Colima",
  DUR: "Durango",
  GUA: "Guanajuato",
  GRO: "Guerrero",
  HID: "Hidalgo",
  JAL: "Jalisco",
  MEX: "México",
  MIC: "Michoacán",
  MOR: "Morelos",
  NAY: "Nayarit",
  NLE: "Nuevo León",
  OAX: "Oaxaca",
  PUE: "Puebla",
  QUE: "Querétaro",
  ROO: "Quintana Roo",
  SLP: "San Luis Potosí",
  SIN: "Sinaloa",
  SON: "Sonora",
  TAB: "Tabasco",
  TAM: "Tamaulipas",
  TLA: "Tlaxcala",
  VER: "Veracruz",
  YUC: "Yucatán",
  ZAC: "Zacatecas",
} as const;

/**
 * The countries Procure supports, keyed by ISO alpha-2 code.
 *
 * This map — not the API's `countries` list — drives the country picker. See
 * `docs/adr/0001-country-resolution-via-iso-code-lookup.md` for why. Adding a
 * country means adding an entry here: a Subdivision list, a Subdivision label
 * and a requiredness rule, none of which the API supplies.
 */
export const SUPPORTED_COUNTRIES = {
  US: {
    isoAlpha2: "US",
    name: "United States",
    subdivisionLabel: "State",
    subdivisionRequired: true,
    subdivisions: toSubdivisions(US_STATES),
    postCodeLabel: "ZIP Code",
    loqateSupported: true,
  },
  CA: {
    isoAlpha2: "CA",
    name: "Canada",
    subdivisionLabel: "Province",
    subdivisionRequired: true,
    subdivisions: toSubdivisions(CANADIAN_PROVINCES),
    postCodeLabel: "Postal Code",
    loqateSupported: true,
  },
  MX: {
    isoAlpha2: "MX",
    name: "Mexico",
    subdivisionLabel: "State",
    subdivisionRequired: true,
    subdivisions: toSubdivisions(MEXICAN_STATES),
    postCodeLabel: "Postal Code",
    loqateSupported: true,
  },
} as const satisfies Record<string, SupportedCountry>;

export type CountryCode = keyof typeof SUPPORTED_COUNTRIES;

/** An API `Country` row, reduced to the two fields we ever read. */
export type ApiCountry = {
  id: string;
  isoAlpha2?: string | null;
};

/**
 * Type guard that checks if a string is a supported ISO alpha-2 country code.
 *
 * @example
 * isSupportedCountryCode("CA") // => true
 * isSupportedCountryCode("GB") // => false
 */
export const isSupportedCountryCode = (code: string | null | undefined): code is CountryCode =>
  Boolean(code) && code! in SUPPORTED_COUNTRIES;

/**
 * Looks up a supported country by ISO alpha-2 code.
 *
 * @returns The country entry, or undefined if Procure does not support it
 */
export const getCountry = (code: string | null | undefined): SupportedCountry | undefined =>
  isSupportedCountryCode(code) ? SUPPORTED_COUNTRIES[code] : undefined;

/**
 * Narrows an ISO alpha-2 code from the API to one Procure supports.
 *
 * A country the map does not know reads as unknown rather than as a
 * confidently wrong one — a stale or foreign country ID never silently becomes
 * a different country.
 */
export const toSupportedCountryCode = (code: string | null | undefined): CountryCode | undefined =>
  isSupportedCountryCode(code) ? code : undefined;

/**
 * Resolves a country's display name through the frontend map, so the picker and
 * the rendered address can never disagree.
 *
 * @param code - ISO alpha-2 code
 * @param defaultValue - Value to display if the country is unknown (defaults to em dash)
 */
export const getCountryName = (code: string | null | undefined, defaultValue = "—"): string =>
  getCountry(code)?.name ?? defaultValue;

/**
 * The Subdivision options for a country's picker, sorted by name.
 *
 * @returns An empty list for an unsupported country
 */
export const getSubdivisionOptions = (code: string | null | undefined): SelectOption[] =>
  (getCountry(code)?.subdivisions ?? [])
    .map((subdivision) => ({ label: subdivision.name, value: subdivision.code }))
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Finds a Subdivision within a country by either its code or its name,
 * case-insensitively. Loqate returns `ON` for Canada but `Jalisco` for Mexico,
 * and legacy records hold whatever the backend was given.
 *
 * @example
 * findSubdivision({ countryCode: "CA", value: "ON" })      // => { code: "ON", name: "Ontario" }
 * findSubdivision({ countryCode: "MX", value: "Jalisco" }) // => { code: "JAL", name: "Jalisco" }
 */
export const findSubdivision = ({
  countryCode,
  value,
}: {
  countryCode: string | null | undefined;
  value: string | null | undefined;
}): Subdivision | undefined => {
  const normalised = value?.trim().toLowerCase();

  if (!normalised) return undefined;

  return getCountry(countryCode)?.subdivisions.find(
    (subdivision) => subdivision.code.toLowerCase() === normalised || subdivision.name.toLowerCase() === normalised,
  );
};

/**
 * The label to show for a stored Subdivision value. A value that is not in the
 * country's list is preserved verbatim rather than silently dropped.
 */
export const resolveSubdivisionLabel = ({
  countryCode,
  value,
}: {
  countryCode: string | null | undefined;
  value: string;
}): string => findSubdivision({ countryCode, value })?.name ?? value;

/**
 * The country's Subdivision options, plus the currently held value when it is
 * not among them. A Contact saved before its country was supported — or given a
 * Subdivision directly in the backend — keeps what it has rather than being
 * silently reset to blank.
 */
export const getSubdivisionOptionsWithValue = ({
  countryCode,
  value,
}: {
  countryCode: string | null | undefined;
  value: string | null | undefined;
}): SelectOption[] => {
  const options = getSubdivisionOptions(countryCode);

  if (!value || options.some((option) => option.value === value)) {
    return options;
  }

  return [{ label: resolveSubdivisionLabel({ countryCode, value }), value }, ...options];
};

/**
 * The countries offered by the picker: the intersection of the frontend map and
 * what the API returned, so a country that cannot be saved is never offered.
 */
export const getCountryOptions = (apiCountries: readonly ApiCountry[] | null | undefined): SelectOption[] =>
  Object.keys(getCountryIdsByCode(apiCountries))
    .map((code) => ({ label: SUPPORTED_COUNTRIES[code as CountryCode].name, value: code }))
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * The code-to-ID lookup used at save time. Country IDs are per-database primary
 * keys, so they are never written down in frontend source.
 */
export const getCountryIdsByCode = (
  apiCountries: readonly ApiCountry[] | null | undefined,
): Partial<Record<CountryCode, string>> =>
  (apiCountries ?? []).reduce<Partial<Record<CountryCode, string>>>((ids, country) => {
    const code = country.isoAlpha2;

    return isSupportedCountryCode(code) && !ids[code] ? { ...ids, [code]: country.id } : ids;
  }, {});

/**
 * Finds a Subdivision by code or name across every supported country. Used
 * where a value arrives without its country — benchmark filters return bare
 * subdivision codes drawn from lots in any country.
 */
export const findSubdivisionInAnyCountry = (value: string | null | undefined): Subdivision | undefined =>
  Object.keys(SUPPORTED_COUNTRIES).reduce<Subdivision | undefined>(
    (found, countryCode) => found ?? findSubdivision({ countryCode, value }),
    undefined,
  );

/**
 * Turns a list of bare Subdivision codes into picker options, labelling each
 * through the supported-country map. A code that cannot be labelled is kept as
 * its own label rather than dropped — dropping it is data loss, not a missing
 * column.
 */
export const getSubdivisionOptionsForCodes = (codes: readonly string[]): SelectOption[] => {
  const uniqueCodes = [...new Set(codes.map((code) => code.trim()).filter(Boolean))];

  return uniqueCodes
    .map((code) => ({ label: findSubdivisionInAnyCountry(code)?.name ?? code, value: code }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * A Subdivision as a table cell: its name alongside the code the row actually
 * holds. A code from a country Procure does not know is still shown — an em
 * dash would hide a real value.
 *
 * @example
 * formatSubdivisionCell("TX") // => "Texas (TX)"
 * formatSubdivisionCell("XX") // => "XX"
 * formatSubdivisionCell(null) // => "—"
 */
export const formatSubdivisionCell = (value: string | null | undefined, defaultValue = "\u2014"): string => {
  const trimmed = value?.trim();

  if (!trimmed) return defaultValue;

  const subdivision = findSubdivisionInAnyCountry(trimmed);

  return subdivision ? `${subdivision.name} (${trimmed})` : trimmed;
};
